import { RefObject, useEffect, useRef, useState } from "react";

import { useSingleFlight } from "@/utils/useSingleFlight";

import { commitCampaignEditors } from "./commitCampaignEditors.util";

import { CampaignPageHandle } from "../types/campaign-page.types";
import { StoredCampaignEntry } from "./campaignRevision.util";

/** Keep the pause and its release attached to the page that actually handed settings over. */
export function useCampaignSettingsHandoff(
	page: RefObject<CampaignPageHandle>,
	campaignUuid: string,
	isOpened: boolean,
	onAcknowledged?: (entry: StoredCampaignEntry) => void
) {
	const latestRef = useRef({ campaignUuid, isOpened });

	latestRef.current = { campaignUuid, isOpened };
	const mountedRef = useRef(true);
	const [owner, setOwner] = useState<{ page: CampaignPageHandle } | null>(null);
	const activeRef = useRef<{ page: CampaignPageHandle } | null>(null);
	const { run } = useSingleFlight();

	useEffect(() => {
		mountedRef.current = true;

		return () => {
			mountedRef.current = false;
			activeRef.current?.page.resumeAutoSave();
			activeRef.current = null;
		};
	}, []);
	useEffect(() => {
		activeRef.current?.page.resumeAutoSave();
		activeRef.current = null;
		setOwner(null);
	}, [campaignUuid, isOpened]);
	const close = () => {
		if (activeRef.current !== owner) {
			return false;
		}

		activeRef.current = null;
		setOwner(null);

		return true;
	};

	return {
		isOpen: owner !== null,
		open: () =>
			run(async () => {
				const handle = page.current;

				if (activeRef.current || !handle) {
					return;
				}

				commitCampaignEditors();
				if (!(await handle.flushAndPause())) {
					return;
				}

				if (
					!mountedRef.current ||
					latestRef.current.campaignUuid !== campaignUuid ||
					!latestRef.current.isOpened
				) {
					handle.resumeAutoSave();

					return;
				}

				const session = { page: handle };

				activeRef.current = session;
				setOwner(session);
			}),
		dismiss: () => {
			if (close()) {
				owner?.page.resumeAutoSave();
			}
		},
		saved: (entry?: StoredCampaignEntry) => {
			if (!close()) {
				return;
			}

			if (entry) {
				onAcknowledged?.(entry);
				owner?.page.resumeAutoSaveAfterSettings(entry);
			} else {
				owner?.page.resumeAutoSave();
			}
		},
	};
}
